// İSG Portal — auth store (Zustand + platform-agnostik persist).
//
// Oturum durumu (access/refresh token + principal snapshot) süreç-içi Zustand'da
// tutulur ve **enjekte edilen** platform depolamasına (`getAuthStorage`)
// kalıcılaştırılır: web IndexedDB (idb), mobil `expo-secure-store`. API client
// token'lara **senkron** (`getState`) erişir; bu yüzden uygulama, route'ları
// render etmeden önce rehydrate'i bekler (`hasHydrated`).
//
// Rehydrate **ertelenir** (`skipHydration: true`): depolama impl'i uygulama giriş
// noktasında `configureAuthStorage(...)` ile enjekte edildikten SONRA, açıkça
// `hydrateAuthStore()` çağrısıyla başlar. Böylece enjeksiyon ↔ hydrate sırası
// deterministiktir (import sırasına bağlı değildir) ve her iki platformda da
// doğru adaptör kullanılır.
//
// Refresh token hassastır; XSS yüzeyini küçültmek için erişim yalnız bu store
// üzerinden yapılır (global/pencere değişkeni yok).

import { create } from 'zustand';
import { type StateStorage, createJSONStorage, persist } from 'zustand/middleware';
import { getAuthStorage } from '../platform/storage';
import { principalFromToken } from './jwt';
// Yalnız tip (runtime döngü yok — `import type` derlemede silinir).
import type { SupplierCompanyItem } from './supplier';
import type { Principal, PrincipalType, TokenPairDto } from './types';

// KRİTİK: `createJSONStorage` factory'yi store KURULUM ANINDA (bir kez) çağırır —
// bu an enjeksiyondan (uygulama giriş noktası) ÖNCEDİR. Bu yüzden depolamayı o an
// yakalamak yerine, her operasyonda enjekte edilmiş adaptöre delege eden **kalıcı
// bir proxy** kullanılır. Böylece `configureAuthStorage(...)` sonrası rehydrate ve
// yazma çağrıları doğru adaptörü (web idb / mobil secure-store) kullanır.
const liveAuthStorage: StateStorage = {
  getItem: (name) => getAuthStorage().getItem(name),
  setItem: (name, value) => getAuthStorage().setItem(name, value),
  removeItem: (name) => getAuthStorage().removeItem(name),
};

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  principal: Principal | null;
  /**
   * Supplier'ın erişebildiği firmalar (id + ad) — firma switcher'ının kaynağı.
   * Yalnız supplier oturumlarında dolar (çok-firma girişindeki `companies` listesi;
   * api-contract §5.4). Aktif firma `principal.companyId`'dir (token'dan). Tek-firma
   * terminal girişte ad gelmez → liste boş/sentetik kalabilir (UI `#id`'ye düşer).
   */
  companies: SupplierCompanyItem[];
  hasHydrated: boolean;

  /** Login/refresh yanıtından oturumu kurar (principal token'dan çözülür). */
  setSession: (tokens: Pick<TokenPairDto, 'accessToken' | 'refreshToken'>) => void;
  /**
   * Supplier terminal oturumunu kurar + erişilebilir firma listesini (switcher
   * için, adlarıyla) saklar. `companies` verilmezse mevcut liste korunur.
   */
  setSupplierSession: (
    tokens: { accessToken: string; refreshToken: string },
    companies?: SupplierCompanyItem[],
  ) => void;
  /**
   * Refresh rotasyonu / firma değişimi sonrası token'ları günceller (firma listesi
   * korunur). `Promise<void>` döner — persist yazımının (idb) GERÇEKTEN bittiği anı
   * işaretler (aşağıdaki implementasyon notuna bkz.). Firma geçişi gibi yazımın
   * kesin bitmesi gereken çağıranlar `await` etmelidir.
   */
  updateTokens: (tokens: { accessToken: string; refreshToken: string }) => Promise<void>;
  /** Oturumu kapatır (logout / refresh başarısız). */
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      principal: null,
      companies: [],
      hasHydrated: false,

      setSession: ({ accessToken, refreshToken }) =>
        set({
          accessToken,
          refreshToken,
          principal: principalFromToken(accessToken),
        }),

      setSupplierSession: ({ accessToken, refreshToken }, companies) =>
        set((state) => ({
          accessToken,
          refreshToken,
          principal: principalFromToken(accessToken),
          companies: companies ?? state.companies,
        })),

      // NOT (kırılgan görünüyor ama DOĞRULANDI — bkz. altındaki not): zustand'ın
      // `persist` middleware'i creator'a verdiği `set` fonksiyonunu SARAR —
      // gerçek implementasyon `set(...args); return setItem();`dir (bkz.
      // `zustand/esm/middleware.mjs` içindeki `persistImpl`), yani `set(...)`
      // çağrısının DÖNÜŞ DEĞERİ storage yazımının promise'idir (idb tabanlı
      // `webIdbAuthStorage.setItem` → `liveAuthStorage` → `createJSONStorage` →
      // persist'in iç `setItem()`'ı → sarılmış `set` — tek, sürekli bir promise
      // zinciri; ara yerde fire-and-forget YOKTUR). Persist'in ORTAK TİP tanımları
      // (`persist.d.ts`) bu dönüşü `unknown`'a düşürür (`PersistReturn` jeneriği
      // her yerde sabit `unknown`) — bu yüzden burada AÇIKÇA `Promise<void>`'a
      // sarılır (tek, belgelenmiş dönüşüm noktası). Doğrulama: kurulu zustand
      // 5.0.14 kaynağı elle okunarak teyit edildi (bkz. plan: switch-company
      // reload fix, gerekçe #1). Firma değişimi tam sayfa reload'dan ÖNCE bu
      // promise'i `await` eder (`auth/supplier.ts` `switchCompany`).
      updateTokens: ({ accessToken, refreshToken }) =>
        set({
          accessToken,
          refreshToken,
          principal: principalFromToken(accessToken),
        }) as unknown as Promise<void>,

      clear: () => set({ accessToken: null, refreshToken: null, principal: null, companies: [] }),
    }),
    {
      name: 'session',
      // Kalıcı proxy: her get/set/remove'da o anki enjekte edilmiş adaptöre delege
      // eder (web idb / mobil secure-store). Enjekte edilmemişse in-memory yedek.
      storage: createJSONStorage(() => liveAuthStorage),
      // Rehydrate ertelenir; app `configureAuthStorage(...)` → `hydrateAuthStore()`.
      skipHydration: true,
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        principal: state.principal,
        companies: state.companies,
      }),
      onRehydrateStorage: () => (state) => {
        // Rehydrate tamamlandı — client artık token'lara güvenle erişebilir.
        useAuthStore.setState({ hasHydrated: true });
        // Token varsa principal'ı tazele (şema değişimine dayanıklılık).
        if (state?.accessToken) {
          useAuthStore.setState({ principal: principalFromToken(state.accessToken) });
        }
      },
    },
  ),
);

// ---- Rehydrate (enjeksiyon sonrası, açık) ----

/**
 * Enjekte edilmiş depolamadan oturumu geri yükler. Uygulama giriş noktası önce
 * `configureAuthStorage(...)` çağırır, sonra bunu `await` eder; tamamlandığında
 * `hasHydrated` true olur (route'lar güvenle render edilir). İki kez çağrılırsa
 * zustand tekrar yükler (idempotent — son durum kazanır).
 */
export async function hydrateAuthStore(): Promise<void> {
  await useAuthStore.persist.rehydrate();
}

// ---- Store dışından senkron erişim (API client için) ----

export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function getRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}

export function isAuthenticated(): boolean {
  return useAuthStore.getState().accessToken !== null;
}

export function principalType(): PrincipalType | null {
  return useAuthStore.getState().principal?.principalType ?? null;
}
